<template>
  <b-card title="Telegram-синхронизация">
    <!-- Обновить ТГ-посты -->
    <div class="mb-2">
      <h5 class="mb-25">Обновить ТГ-посты</h5>
      <b-card-text class="text-muted mb-1">
        Обновляет посты игр в Telegram-канале.
      </b-card-text>
      <b-button
          variant="primary"
          :disabled="isUpdatingPosts"
          @click="updatePosts"
      >
        <b-spinner v-if="isUpdatingPosts" small class="mr-50" />
        <feather-icon v-else icon="RefreshCwIcon" class="mr-50" />
        Обновить посты
      </b-button>
      <pre v-if="postsResult" class="mt-1 mb-0 p-1 bg-light rounded">{{ postsResult }}</pre>
    </div>

    <hr>

    <!-- Синхронизировать напоминалки -->
    <div class="my-2">
      <h5 class="mb-25">Синхронизировать напоминалки</h5>
      <b-card-text class="text-muted mb-1">
        Принудительная синхронизация напоминалок об играх (аналог tg:sync-reminders --force=1).
      </b-card-text>
      <b-button
          variant="warning"
          :disabled="isSyncingReminders"
          @click="syncReminders"
      >
        <b-spinner v-if="isSyncingReminders" small class="mr-50" />
        <feather-icon v-else icon="BellIcon" class="mr-50" />
        Синхронизировать напоминалки
      </b-button>
      <pre v-if="remindersResult" class="mt-1 mb-0 p-1 bg-light rounded">{{ remindersResult }}</pre>
    </div>

    <hr>

    <!-- Сбросить кеш блокировки -->
    <div class="mt-2">
      <h5 class="mb-25">Сбросить кеш блокировки</h5>
      <b-card-text class="text-muted mb-1">
        Сбрасывает кеш блокировки синхронизации напоминалок.
      </b-card-text>
      <b-button
          variant="outline-danger"
          :disabled="isResettingCache"
          @click="resetCache"
      >
        <b-spinner v-if="isResettingCache" small class="mr-50" />
        <feather-icon v-else icon="Trash2Icon" class="mr-50" />
        Сбросить кеш
      </b-button>
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardText,
  BButton,
  BSpinner,
} from 'bootstrap-vue'
import { onUnmounted, ref } from '@vue/composition-api'
import store from '@/store'
import gameStoreModule from '@/views/game/gameStoreModule'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BCardText,
    BButton,
    BSpinner,
  },
  setup(props, { root }) {
    const STORE_MODULE_NAME = 'app-games'
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, gameStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const toast = useToast()

    const isUpdatingPosts = ref(false)
    const isSyncingReminders = ref(false)
    const isResettingCache = ref(false)
    const postsResult = ref('')
    const remindersResult = ref('')

    const showToast = (title, variant) => {
      toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'danger' ? 'AlertTriangleIcon' : 'CheckCircleIcon',
          variant,
        },
      })
    }

    const errorMessage = (error, fallback) => (
      (error.response && error.response.data && error.response.data.message) || fallback
    )

    const confirm = (title, text) => root.$swal({
      title,
      text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Выполнить',
      cancelButtonText: 'Отмена',
      customClass: {
        confirmButton: 'btn btn-success',
        cancelButton: 'btn btn-outline-secondary ml-1',
      },
      buttonsStyling: false,
    })

    const updatePosts = () => {
      confirm('Обновить посты в канале?', 'Посты игр будут обновлены в Telegram-канале').then(result => {
        if (!result.value) return

        isUpdatingPosts.value = true
        postsResult.value = ''
        store.dispatch('app-games/updateTelegramPosts')
            .then(response => {
              postsResult.value = response.data.text || ''
              showToast('Посты обновлены', 'success')
            })
            .catch(error => showToast(errorMessage(error, 'Ошибка обновления постов'), 'danger'))
            .finally(() => {
              isUpdatingPosts.value = false
            })
      })
    }

    const syncReminders = () => {
      isSyncingReminders.value = true
      remindersResult.value = ''
      store.dispatch('app-games/syncReminders')
          .then(response => {
            remindersResult.value = response.data.text || ''
            showToast('Напоминалки синхронизированы', 'success')
          })
          .catch(error => showToast(errorMessage(error, 'Ошибка синхронизации напоминалок'), 'danger'))
          .finally(() => {
            isSyncingReminders.value = false
          })
    }

    const resetCache = () => {
      confirm('Сбросить кеш блокировки?', 'Кеш блокировки синхронизации напоминалок будет сброшен').then(result => {
        if (!result.value) return

        isResettingCache.value = true
        store.dispatch('app-games/resetRemindersCache')
            .then(() => showToast('Кеш сброшен', 'success'))
            .catch(error => showToast(errorMessage(error, 'Ошибка сброса кеша'), 'danger'))
            .finally(() => {
              isResettingCache.value = false
            })
      })
    }

    return {
      isUpdatingPosts,
      isSyncingReminders,
      isResettingCache,
      postsResult,
      remindersResult,
      updatePosts,
      syncReminders,
      resetCache,
    }
  },
}
</script>
